import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'motion/react';
import { MapPin, Plus, Check, Wallet, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';

import {
  addressSchema,
  type AddressInput,
  formatRupees,
  lineTotal,
  deliveryFeeFor,
  UNIT_LABELS,
  type UnitOfMeasure,
} from '@sreerasa/shared';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/features/cart/cart-store';
import { useAddresses, useCreateAddress, usePlaceOrder, useProducts } from '@/lib/queries';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/checkout')({
  // Gate before render, not after — a checkout form that appears and then
  // fails at submit is worse than never showing it.
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: '/auth/sign-in', search: { redirect: '/checkout' } });
    }
  },
  component: CheckoutPage,
});

const SLOTS = ['Morning (7–11am)', 'Afternoon (12–4pm)', 'Evening (5–9pm)'] as const;

function CheckoutPage() {
  const navigate = useNavigate();
  const { lines, clear } = useCart();
  const { data: addresses, isLoading: addressesLoading } = useAddresses();
  const { data: products } = useProducts({});
  const createAddress = useCreateAddress();
  const placeOrder = usePlaceOrder();

  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [slot, setSlot] = useState<string>(SLOTS[0]);
  const [notes, setNotes] = useState('');

  const form = useForm<AddressInput>({
    resolver: zodResolver(addressSchema),
    defaultValues: { label: 'Home', state: 'Telangana', is_default: true },
  });

  const byId = new Map(products?.map((p) => [p.id, p]));
  const priced = lines.map((line) => {
    const product = byId.get(line.product_id);
    return { ...line, product, total: product ? lineTotal(product.price_paise, line.quantity) : 0 };
  });

  const subtotal = priced.reduce((sum, item) => sum + item.total, 0);
  const delivery = deliveryFeeFor(subtotal);
  const total = subtotal + delivery;

  // Default to the user's default address once addresses land.
  const activeAddress =
    selectedAddress ?? addresses?.find((a) => a.is_default)?.id ?? addresses?.[0]?.id ?? null;

  async function handleCreateAddress(values: AddressInput) {
    try {
      const created = await createAddress.mutateAsync({
        ...values,
        line2: values.line2 || null,
        landmark: values.landmark || null,
        district: values.district || null,
        latitude: null,
        longitude: null,
      } as never);
      setSelectedAddress(created.id);
      setShowAddressForm(false);
      toast.success('Address saved');
    } catch (error) {
      toast.error('Could not save that address', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  async function handlePlaceOrder() {
    if (!activeAddress) {
      toast.error('Choose a delivery address first');
      return;
    }

    try {
      const result = await placeOrder.mutateAsync({
        address_id: activeAddress,
        payment_method: 'cod',
        delivery_slot: slot,
        delivery_notes: notes || undefined,
      });

      // Only clear locally after the server has confirmed. The server already
      // emptied its own cart inside the place_order transaction.
      clear();
      toast.success(`Order ${result.order_number} placed`, {
        description: 'The team has been notified. You will hear from us shortly.',
      });
      void navigate({ to: '/orders/$id', params: { id: result.order_id } });
    } catch (error) {
      // These messages come from our own RAISE statements — "Only 3 left of
      // Banganapalli Mangoes" — so they're worth showing verbatim.
      toast.error('Could not place the order', {
        description: error instanceof Error ? error.message : 'Try again in a moment.',
      });
    }
  }

  if (lines.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-28 text-center">
        <h1 className="font-display text-2xl font-bold text-soil-900">Nothing to check out</h1>
        <p className="mt-2 text-soil-600">Your basket is empty.</p>
        <Button className="mt-6" onClick={() => navigate({ to: '/shop' })}>
          Browse the shop
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-display text-display font-black text-soil-900">Checkout</h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-6">
          {/* ── Address ── */}
          <section className="rounded-card border border-soil-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-soil-900">
                <MapPin className="size-4 text-farm-600" aria-hidden />
                Deliver to
              </h2>
              {!showAddressForm && (addresses?.length ?? 0) > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setShowAddressForm(true)}>
                  <Plus aria-hidden /> New address
                </Button>
              )}
            </div>

            {addressesLoading ? (
              <div className="mt-4 space-y-2">
                <div className="skeleton h-20 rounded-xl" />
              </div>
            ) : showAddressForm || (addresses?.length ?? 0) === 0 ? (
              <form
                noValidate
                onSubmit={form.handleSubmit(handleCreateAddress)}
                className="mt-4 space-y-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Recipient name"
                    required
                    {...form.register('recipient_name')}
                    error={form.formState.errors.recipient_name?.message}
                  />
                  <Input
                    label="Phone"
                    required
                    placeholder="+919876543210"
                    hint="We'll call before the delivery"
                    {...form.register('phone')}
                    error={form.formState.errors.phone?.message}
                  />
                </div>

                <Input
                  label="Address"
                  required
                  placeholder="Flat / house, building, street"
                  {...form.register('line1')}
                  error={form.formState.errors.line1?.message}
                />
                <Input label="Area / locality" {...form.register('line2')} />
                <Input
                  label="Landmark"
                  placeholder="Opposite the temple, next to the school…"
                  {...form.register('landmark')}
                />

                <div className="grid gap-4 sm:grid-cols-3">
                  <Input
                    label="City"
                    required
                    {...form.register('city')}
                    error={form.formState.errors.city?.message}
                  />
                  <Input
                    label="State"
                    required
                    {...form.register('state')}
                    error={form.formState.errors.state?.message}
                  />
                  <Input
                    label="Pincode"
                    required
                    inputMode="numeric"
                    {...form.register('pincode')}
                    error={form.formState.errors.pincode?.message}
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" isLoading={createAddress.isPending}>
                    Save address
                  </Button>
                  {(addresses?.length ?? 0) > 0 && (
                    <Button type="button" variant="ghost" onClick={() => setShowAddressForm(false)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            ) : (
              <div className="mt-4 space-y-2.5">
                {addresses?.map((address) => (
                  <motion.button
                    key={address.id}
                    type="button"
                    onClick={() => setSelectedAddress(address.id)}
                    whileTap={{ scale: 0.99 }}
                    aria-pressed={activeAddress === address.id}
                    className={cn(
                      'flex w-full gap-3 rounded-xl border p-4 text-left transition-all',
                      activeAddress === address.id
                        ? 'border-farm-600 bg-farm-50 ring-2 ring-farm-600/15'
                        : 'border-soil-200 hover:border-soil-300',
                    )}
                  >
                    <div
                      className={cn(
                        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        activeAddress === address.id
                          ? 'border-farm-600 bg-farm-600'
                          : 'border-soil-300',
                      )}
                    >
                      {activeAddress === address.id && (
                        <Check className="size-3 text-white" aria-hidden />
                      )}
                    </div>

                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium text-soil-900">
                        {address.recipient_name}
                        <Badge variant="neutral" size="sm">
                          {address.label}
                        </Badge>
                      </p>
                      <p className="mt-0.5 text-sm text-soil-600">
                        {[address.line1, address.line2, address.landmark]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                      <p className="text-sm text-soil-600">
                        {address.city}, {address.state} {address.pincode}
                      </p>
                      <p className="mt-1 text-xs text-soil-500">{address.phone}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </section>

          {/* ── Slot ── */}
          <section className="rounded-card border border-soil-200 bg-white p-5">
            <h2 className="font-display text-lg font-bold text-soil-900">When suits you?</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {SLOTS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setSlot(option)}
                  aria-pressed={slot === option}
                  className={cn(
                    'rounded-pill px-4 py-2 text-sm font-medium transition-all active:scale-95',
                    slot === option
                      ? 'bg-farm-600 text-white shadow-soft'
                      : 'bg-soil-100 text-soil-700 hover:bg-soil-200',
                  )}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="mt-5">
              <Textarea
                label="Anything we should know?"
                placeholder="Gate code, which floor, call before you arrive…"
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                maxLength={500}
              />
            </div>
          </section>

          {/* ── Payment ── */}
          <section className="rounded-card border border-soil-200 bg-white p-5">
            <h2 className="font-display text-lg font-bold text-soil-900">Payment</h2>

            <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-farm-600 bg-farm-50 p-4">
              <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-farm-600">
                <Check className="size-3 text-white" aria-hidden />
              </div>
              <div>
                <p className="flex items-center gap-2 font-medium text-soil-900">
                  <Wallet className="size-4 text-farm-600" aria-hidden />
                  Cash or UPI on delivery
                </p>
                <p className="mt-1 text-sm text-soil-600">
                  You pay once the produce is in your hands and you've looked at it. Online payment
                  is coming.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* ── Summary ── */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-card border border-soil-200 bg-white p-5">
            <h2 className="font-display text-lg font-bold text-soil-900">Your order</h2>

            <ul className="mt-4 space-y-3">
              {priced.map((item) => (
                <li key={item.product_id} className="flex justify-between gap-3 text-sm">
                  <span className="min-w-0 text-soil-700">
                    <span className="line-clamp-1 font-medium">{item.title}</span>
                    <span className="text-xs text-soil-500">
                      {item.quantity} {UNIT_LABELS[item.unit as UnitOfMeasure] ?? item.unit}
                    </span>
                  </span>
                  <span className="shrink-0 font-medium tabular-nums">
                    {formatRupees(item.total)}
                  </span>
                </li>
              ))}
            </ul>

            <dl className="mt-4 space-y-2 border-t border-soil-100 pt-4 text-sm">
              <div className="flex justify-between">
                <dt className="text-soil-600">Subtotal</dt>
                <dd className="tabular-nums">{formatRupees(subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-soil-600">Delivery</dt>
                <dd className="tabular-nums">
                  {delivery === 0 ? (
                    <span className="text-farm-600">Free</span>
                  ) : (
                    formatRupees(delivery)
                  )}
                </dd>
              </div>
            </dl>

            <div className="mt-4 flex items-baseline justify-between border-t border-soil-100 pt-4">
              <span className="font-display font-bold text-soil-900">Total</span>
              <span className="font-display text-2xl font-black text-soil-900">
                {formatRupees(total)}
              </span>
            </div>

            <Button
              size="lg"
              full
              className="mt-5"
              onClick={handlePlaceOrder}
              isLoading={placeOrder.isPending}
              loadingText="Placing your order…"
              disabled={!activeAddress}
            >
              <PackageCheck aria-hidden />
              Place order
            </Button>

            <p className="mt-3 text-center text-xs leading-relaxed text-soil-500">
              We'll confirm by email and call before delivering.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
